import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { heritageWalks } from '../utils/mockData';
import { Clock, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export const HeritageWalksPage = () => {
  const { language } = useLanguage();

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = 'Heritage Walks — Delhi Walking Tours | POMTrips';
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <Header onRequestItinerary={() => {}} />

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_78c28fa2-98c0-4ed4-9e64-bb9cb7262686/artifacts/o91lpwp8_20170516_Old%20Delhi%20%282%29.jpg"
          alt="Heritage Walks Delhi"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4">
              Heritage Walks
            </h1>
            <p className="text-xl text-white/90 max-w-2xl">
              Discover Delhi on foot - intimate walking tours through history, culture, art, and spirituality
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-[#2C2C2C] mb-6">
            Walk Through Living History
          </h2>
          <p className="text-lg text-[#8B7D6B] leading-relaxed mb-8">
            Our heritage walks offer an intimate way to experience Delhi's rich tapestry of cultures, cuisines, and communities. Led by expert local guides, these small-group walks (maximum 8-10 people) take you beyond tourist sites into authentic neighborhoods, markets, and spiritual spaces. Each walk is designed to create meaningful connections with Delhi's past and present.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-[#C4B5A0] mb-2">3</div>
              <div className="text-sm text-[#8B7D6B]">Unique Walks</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#C4B5A0] mb-2">3-4</div>
              <div className="text-sm text-[#8B7D6B]">Hours Duration</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#C4B5A0] mb-2">8-10</div>
              <div className="text-sm text-[#8B7D6B]">Max Group Size</div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage Walks Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {heritageWalks.map((walk, index) => (
              <Link
                key={walk.id}
                to={`/heritage-walks/${walk.slug}`}
                className="group block fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={walk.heroImage}
                      alt={language === 'en' ? walk.name : walk.nameEs}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-serif text-2xl text-white mb-1">
                        {language === 'en' ? walk.name : walk.nameEs}
                      </h3>
                      <p className="text-white/90 text-sm italic">
                        {language === 'en' ? walk.tagline : walk.taglineEs}
                      </p>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4 text-sm text-[#8B7D6B]">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {language === 'en' ? walk.duration : walk.durationEs}
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        Max 8-10
                      </div>
                    </div>

                    <p className="text-[#8B7D6B] mb-4 line-clamp-3">
                      {language === 'en' ? walk.summary : walk.summaryEs}
                    </p>

                    <div className="flex items-center text-[#C4B5A0] font-medium">
                      <span>View Walk Details</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#6B4E37] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl mb-4">
            Ready to Explore Delhi on Foot?
          </h2>
          <p className="text-lg mb-8 text-white/90">
            Join us for an unforgettable walking experience through Delhi's heritage, culture, and communities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/917827884419?text=Hi!%20I'm%20interested%20in%20the%20Heritage%20Walks.%20Please%20share%20details."
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-6 text-lg">
                Chat on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
